import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getFeed } from '../utils/api'
import { C, gradient, fontStack, btn } from '../constant/theme'
import Navbar from '../Components/Navbar'

// Home feed for any signed-in user: a flat list of every trip. Clicking a card
// opens the trip's timeline.
export default function Feed() {
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [mobile, setMobile] = useState(window.innerWidth < 640)

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 640)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    getFeed()
      .then(setItems)
      .catch((e) => setError(e?.response?.data?.message || 'Could not load trips.'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div style={S.root}>
      <Navbar />

      <div style={S.hero}>
        <div style={{ ...S.heroInner, padding: mobile ? '28px 16px' : '44px 24px' }}>
          <h1 style={{ ...S.heroTitle, fontSize: mobile ? 24 : 32 }}>Trips</h1>
          <p style={S.heroSub}>Browse every group's tours and trips. Open any one to see its day-by-day timeline, photos and videos.</p>
        </div>
      </div>

      <main style={{ ...S.main, padding: mobile ? '20px 14px' : '28px 24px' }}>
        {loading ? (
          <p style={S.muted}>Loading trips…</p>
        ) : error ? (
          <p style={S.muted}>{error}</p>
        ) : items.length === 0 ? (
          <div style={S.empty}>No trips have been added yet.</div>
        ) : (
          <div style={S.grid}>
            {items.map((t) => (
              <TripCard key={t.slug} trip={t} onOpen={() => navigate(`/g/${t.slug}`)} />
            ))}
          </div>
        )}
      </main>

      <footer style={S.footer}>Made with 🧭 Tour Timeline</footer>
    </div>
  )
}

function TripCard({ trip, onOpen }) {
  const cover = trip.coverImage?.url
  return (
    <div style={S.card} onClick={onOpen}>
      <div style={{ ...S.cover, backgroundImage: cover ? `url(${cover})` : gradient }} />
      <div style={S.cardBody}>
        <strong style={S.cardTitle}>{trip.name}</strong>
        <div style={S.meta}>
          {trip.locationName ? `📍 ${trip.locationName}` : ''}
          {trip.startDate ? `${trip.locationName ? ' · ' : ''}🗓 ${[trip.startDate, trip.endDate].filter(Boolean).join(' → ')}` : ''}
        </div>
        {trip.summary && <p style={S.summary}>{trip.summary}</p>}
        <div style={S.counts}>
          <span>{trip.counts?.days || 0} {(trip.counts?.days || 0) === 1 ? 'day' : 'days'}</span>
          <span style={S.dot}>·</span>
          <span>{trip.counts?.media || 0} {(trip.counts?.media || 0) === 1 ? 'photo/video' : 'photos & videos'}</span>
        </div>
        <button style={{ ...btn('primary'), marginTop: 14, width: '100%' }} onClick={(e) => { e.stopPropagation(); onOpen() }}>
          View trip
        </button>
      </div>
    </div>
  )
}

const S = {
  root: { minHeight: '100vh', background: C.bg, fontFamily: fontStack, display: 'flex', flexDirection: 'column' },
  hero: { background: gradient },
  heroInner: { maxWidth: 1040, margin: '0 auto', width: '100%', padding: '44px 24px', color: '#fff' },
  heroTitle: { fontSize: 32, fontWeight: 800, margin: '0 0 10px', letterSpacing: '-0.5px' },
  heroSub: { fontSize: 15, color: 'rgba(220,232,245,0.8)', margin: 0, maxWidth: 560, lineHeight: 1.6 },
  main: { maxWidth: 1040, margin: '0 auto', width: '100%', padding: '28px 24px', flex: 1, boxSizing: 'border-box' },
  muted: { color: C.textSoft },
  empty: { background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 32, textAlign: 'center', color: C.textSoft },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18 },
  card: { background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, overflow: 'hidden', cursor: 'pointer', boxShadow: '0 2px 10px rgba(13,27,42,.06)', display: 'flex', flexDirection: 'column' },
  cover: { height: 150, backgroundSize: 'cover', backgroundPosition: 'center' },
  cardBody: { padding: 16, display: 'flex', flexDirection: 'column', flex: 1 },
  cardTitle: { color: C.text, fontSize: 16.5 },
  meta: { fontSize: 13, color: C.textSoft, marginTop: 8 },
  summary: { fontSize: 13.5, color: '#33404f', lineHeight: 1.55, margin: '10px 0 0', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  counts: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: C.muted, marginTop: 10, fontWeight: 600 },
  dot: { color: C.muted },
  footer: { textAlign: 'center', color: C.muted, fontSize: 13, padding: '24px 0' },
}
